import { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import constants from "../../../shared/constants";
import storageConstants from "../../../shared/constants/storage";
import { removeSessionStorage, setSessionStorage } from "../../../shared/utils/sessionStorage";
import { reloadApp, trapFocus } from "../../../shared/utils";
import { trackMediaEnd } from "../../../shared/analytics/media";
import { usePlayerActionUtils } from "./playerUtils";
import { useReducers } from "../../../shared/hooks/useReducer";

const { REDUCER_TYPE } = constants;
const { USER_HAS_CLICK, PLAYER_LOADED } = storageConstants;

export const usePlayerMountEffect = ({
  isOnDemandType,
  playbackType,
  setPlayNextProgram,
  playButtonRef,
  volumeButtonRef,
  playerLoaded,
  isFreshLogin,
  cancelTokenSource,
  bookmarkTimer,
  videoControlsTimer,
  autoPlayTimer,
  controlPlaybackDeltaTimer,
  handleAVSStopContent,
  setCCList,
}) => {
  const { toggleSpinningLoader, updateVideoPlayingState } = usePlayerActionUtils();
  const history = useHistory();
  const { featureToggles } = useReducers(REDUCER_TYPE.APP);
  const { isDynatraceTrackingEnabled } = featureToggles;

  useEffect(() => {
    if (isOnDemandType) {
      playbackType !== "episode" && setPlayNextProgram(false);
      playButtonRef.current && playButtonRef.current.focus();
    } else {
      volumeButtonRef.current && volumeButtonRef.current.focus();
    }
    if (playerLoaded && playerLoaded === "true") {
      if (window.location.hash.includes("player")) {
        if (isFreshLogin && isFreshLogin === "true") {
          history.push("/");
        } else {
          history.goBack();
        }
        removeSessionStorage(PLAYER_LOADED);
      }
    } else {
      setSessionStorage(PLAYER_LOADED, "true");
    }
    const unmountTokenSource = cancelTokenSource.current;
    return () => {
      updateVideoPlayingState({ playing: false });
      if (bookmarkTimer.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearInterval(bookmarkTimer.current);
      }
      if (videoControlsTimer.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearTimeout(videoControlsTimer.current);
      }

      if (autoPlayTimer.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearTimeout(autoPlayTimer.current);
      }

      trackMediaEnd(null, isDynatraceTrackingEnabled);

      // make the stop content call when component is unmounted
      controlPlaybackDeltaTimer(false);
      handleAVSStopContent();
      removeSessionStorage(PLAYER_LOADED);
      setCCList([]);
      toggleSpinningLoader(false);
      unmountTokenSource.cancel(); // silent cancellation of middleware requests
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const useReloadAppEffect = (reloadAppFlag, appProvider) => {
  const shouldReloadApp = useRef(false);
  useEffect(() => {
    shouldReloadApp.current = reloadAppFlag;
    return () => {
      if (shouldReloadApp.current) {
        reloadApp(appProvider);
      }
    };
  }, [appProvider, reloadAppFlag]);
};

export const useContentIdEffect = ({
  controlPlaybackDeltaTimer,
  handleAVSStopContent,
  hasCalledStopContent,
  activeContentId,
  currentContentId,
  isSafari,
  getSessionStorage,
  setUserHasClick,
}) => {
  useEffect(() => {
    controlPlaybackDeltaTimer(false);
    // When currentContentId changes, we need to perform an AVSStopContent call and update the activeContentId
    if (!hasCalledStopContent.current && activeContentId.current !== currentContentId) {
      handleAVSStopContent(false, currentContentId);
    }

    if ((isSafari && getSessionStorage(USER_HAS_CLICK) === true) || !isSafari) {
      setUserHasClick(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentContentId]);
};

export const usePCBlockEffect = (PCBlocked, blockPlayback) => {
  useEffect(() => {
    if (!PCBlocked) {
      blockPlayback.current = false;
    } else {
      blockPlayback.current = true;
    }
  }, [PCBlocked, blockPlayback]);
};

export const useFocusOnSideNavOpen = (videoWrapper, isSideNavOpen) => {
  useEffect(() => {
    if (isSideNavOpen) {
      videoWrapper.current.focus();
    }
  }, [isSideNavOpen, videoWrapper]);
};

export const useStillWatchingPromptEffect = (
  showStillWatchingPrompt,
  setShowNextEpisodeCard,
  autoPlayTimer,
  stillWatchingCard,
  setVideoControlsStyle
) => {
  useEffect(() => {
    if (showStillWatchingPrompt) {
      // cancel binge watch
      setShowNextEpisodeCard(false);
      if (autoPlayTimer.current) {
        clearTimeout(autoPlayTimer.current);
      }

      // when showing the still watching prompt, hide controls and focus the prompt
      if (stillWatchingCard.current) {
        setVideoControlsStyle({ opacity: 0, visibility: "hidden" });
        trapFocus(stillWatchingCard.current);
        stillWatchingCard.current.focus();
      }
    }
  }, [showStillWatchingPrompt, setShowNextEpisodeCard, autoPlayTimer, stillWatchingCard, setVideoControlsStyle]);
};

export const useCheckPauseLiveAllowed = (liveProgramDetails, isPauseLiveTVEnabled, isPauseLiveAllowed) => {
  useEffect(() => {
    if (liveProgramDetails) {
      if (isPauseLiveTVEnabled && liveProgramDetails.channel?.metadata?.isTimeshift) {
        isPauseLiveAllowed.current = true;
      } else {
        isPauseLiveAllowed.current = false;
      }
    }
  }, [liveProgramDetails, isPauseLiveTVEnabled, isPauseLiveAllowed]);
};
